import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import ShareModal from 'components/Common/Modals/ShareModal'
import OfferBNPLAd from '../OfferBNPLAd'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import ShareButton from 'components/Common/ShareWithFriendsButton'
import FlashOfferSummary from '../FlashHotel/FlashOfferSummary'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import DepositsAvailableTag from 'components/Common/SecureWithDeposit/DepositsAvailableTag'
import { getDepositAmountPercentage, isDepositAvailableOnOffer } from 'lib/payment/depositsUtils'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import OfferLabels from 'components/Common/Labels/OfferLabels'
import GeoContext from 'contexts/geoContext'
import {
  getCheapestAvailablePackageOption,
  getCheapestSelectedPackageOption,
  OfferPackageOptionView,
  getTacticalPackageOptions,
} from 'selectors/offerPage/offerPageSelectors'
import UltraLuxUSPs from './UltraLuxUSPs'
import AlwaysOnSummary from '../AlwaysOn/AlwaysOnSummary'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { useBusinessTravellerStickyBudgetBarHeightContext } from 'businessTraveller/utils/useBusinessBudgetBarHeightContext'
import UltraLuxPropertyDetails from './UltraLuxPropertyDetails'
import { ShareConfig } from 'components/Common/Modals/ShareModal/ShareModal'
import Group from 'components/utils/Group'
import moment from 'moment'
import { OFFER_VIEWS_THRESHOLD } from 'constants/offer'
import OfferViewsUrgencyBanner from '../OfferViewsUrgencyBanner'

const ContentColumn = styled.div`
  > * + * {
    margin-top: ${rem(16)};
  }
`

const OfferIntroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${rem(12)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr min-content;
    grid-gap: ${rem(16)} ${rem(48)};
  }
`

const SummaryColumn = styled.div`
  display: none;

  ${mediaQueryUp.tablet} {
    display: flex;
    flex-direction: column;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const SummaryBlock = styled.div`
  ${mediaQueryUp.tablet} {
    position: sticky;
    top: ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const HotelSummary = styled(FlashOfferSummary)`
  min-width: ${rem(308)};

  ${mediaQueryUp.desktop} {
    min-width: ${rem(382)};
  }
`

const OfferIntroWrapper = styled(LayoutContainer)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: ${rem(20)} ${rem(40)};
  }

  ${mediaQueryUp.desktop} {
    padding: ${rem(32)} ${rem(12)};
  }

  > * + * {
    margin-top: ${rem(48)};
  }
`

const MobileOfferViews = styled(OfferViewsUrgencyBanner)`
  position: relative;
  top: -${rem(20)};
  margin: 0 -${rem(20)};
`

interface Props {
  cheapestSelectedOption?: OfferPackageOptionView;
  cheapestOption: OfferPackageOptionView;
  promotions: Array<OfferPackageOptionView>;
  checkIn?: string;
  offer: App.HotelOffer;
  className?: string;
  onViewPackageOptions: () => void;
  flightDestination?: App.Airport;
  defaultDepositAmountPercentage: number;
  shareModalConfig: ShareConfig;
}

function UltraLuxIntro(props: Props) {
  const {
    cheapestOption,
    promotions,
    cheapestSelectedOption,
    checkIn,
    offer,
    className,
    onViewPackageOptions,
    flightDestination,
    defaultDepositAmountPercentage,
    shareModalConfig,
  } = props

  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)
  const property = offer.property
  const location = [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')

  const allPrices = useMemo(() => offer.packages.map(p => p.price), [offer.packages])
  const { currentCurrency } = useContext(GeoContext)

  const packageView = cheapestSelectedOption ?? cheapestOption
  const { hotelPrice, propertyFees } = packageView
  const totalPrice = hotelPrice + (propertyFees ?? 0)

  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const depositsAvailable = isDepositAvailableOnOffer(offer, currentCurrency, checkInMoment, totalPrice)

  const businessTravellerStickyBudgetBarStyle = useBusinessTravellerStickyBudgetBarHeightContext()
  const showViewsCount = offer.stats.views > OFFER_VIEWS_THRESHOLD

  return (<OfferIntroWrapper>
    {showViewsCount && <CSSBreakpoint max="mobile">
      <MobileOfferViews offer={offer} />
    </CSSBreakpoint> }
    <OfferIntroGrid className={className} data-testid="offer-intro">
      <ContentColumn>
        <LabelGroup>
          {(depositsAvailable) && <DepositsAvailableTag offerType={offer.type} depositAmountPercentage={depositAmountPercentage} depositThresholds={offer.depositThresholds}/>}
          <OfferLabels offer={offer} showLimitedAvailability />
        </LabelGroup>
        <UltraLuxPropertyDetails
          offer={offer}
          property={property}
          location={location}
          promotions={promotions}
        />
        <UltraLuxUSPs />
      </ContentColumn>
      <SummaryColumn>
        <CSSBreakpoint min="tablet">
          <Group direction="horizontal" verticalAlign="center" gap={8} horizontalAlign="end">
            <TripGuard inverse>
              <ShareModal shareConfig={shareModalConfig}>
                <ShareButton outdent="right" />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <ShareModal shareConfig={shareModalConfig}>
                <ShareButton />
              </ShareModal>
            </TripGuard>
            <TripGuard>
              <BookmarkButton offer={offer} />
            </TripGuard>
          </Group>
        </CSSBreakpoint>
        <SummaryBlock style={businessTravellerStickyBudgetBarStyle}>
          <div>
            {showViewsCount && <OfferViewsUrgencyBanner offer={offer} /> }
            <HotelSummary
              as={offer.type === 'tactical_ao_hotel' ? AlwaysOnSummary : undefined}
              onViewPackageOptions={onViewPackageOptions}
              flightDestination={flightDestination}
            />
          </div>
          <OfferBNPLAd prices={allPrices} />
        </SummaryBlock>
      </SummaryColumn>
    </OfferIntroGrid>
  </OfferIntroWrapper>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  promotions: getTacticalPackageOptions(state, appState),
  cheapestSelectedOption: getCheapestSelectedPackageOption(appState, state),
  cheapestOption: getCheapestAvailablePackageOption(state, appState),
  checkIn: state.checkInDate,
  offer: state.offer,
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(appState),
}))(UltraLuxIntro)
